import { PencilLine, UserRoundPen } from "lucide-react";

import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button";

interface ScorerBannerProps {
  hasActiveGame: boolean;
  isScorer: boolean;
  scorerName: string | null;
  scoringClaimed: boolean;
  onClaimScoring: () => void;
  pending?: boolean;
  unknown?: boolean;
}

export function ScorerBanner({
  hasActiveGame,
  isScorer,
  scorerName,
  scoringClaimed,
  onClaimScoring,
  pending = false,
  unknown = false,
}: ScorerBannerProps) {
  if (!hasActiveGame) return null;
  const state = isScorer ? "mine" : scoringClaimed ? "theirs" : "unclaimed";
  const label =
    state === "mine"
      ? "You are scoring"
      : state === "theirs"
        ? `${scorerName ?? "Someone else"} is scoring`
        : "Scoring is unclaimed — tap to score";
  return (
    <div
      aria-live="polite"
      className={cn(
        "no-print font-mono text-[0.72rem] font-bold tracking-[0.12em] uppercase",
        state === "mine" && "bg-success/10 text-success",
        state === "theirs" && "bg-warning/10 text-warning",
        state === "unclaimed" && "bg-surface-raised/60 text-foreground-muted",
      )}
    >
      <div className="mx-auto flex min-h-9 max-w-[var(--content-width)] items-center justify-center gap-2 px-[max(0.75rem,var(--safe-left))] py-1 pr-[max(0.75rem,var(--safe-right))]">
        <PencilLine className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{label}</span>
        {state === "mine" ? null : (
          <Button
            type="button"
            size="xs"
            variant={state === "theirs" ? "selected" : "outline"}
            className="ml-1 font-mono tracking-[0.12em] uppercase"
            onClick={onClaimScoring}
            pending={pending}
            pendingLabel={unknown ? "CHECKING…" : "CLAIMING…"}
          >
            <UserRoundPen className="size-3.5" aria-hidden="true" />
            {state === "theirs" ? "Take over" : "Score"}
          </Button>
        )}
      </div>
    </div>
  );
}
